import { Heart, Globe, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { MatchSource } from "@/hooks/useUnifiedMatches";

interface MatchesEmptyStateProps {
  filter: MatchSource;
}

const EMPTY_STATES: Record<MatchSource, {
  icon: React.ReactNode;
  title: string;
  description: string;
  cta: string;
  link: string;
  gradient: string;
}> = {
  all: {
    icon: <Heart className="w-12 h-12" />,
    title: "Your matches will appear here",
    description: "Start connecting with people across Pop Balloon, Global Connect, and Love Exhibition",
    cta: "Start Discovering",
    link: "/",
    gradient: "from-pink-500/20 to-purple-500/20",
  },
  exhibition: {
    icon: <Sparkles className="w-12 h-12" />,
    title: "No Live Dating Event matches yet",
    description: "Join a Live Dating Event stage to meet people from around the world in real-time",
    cta: "Enter Live Dating Event",
    link: "/love-exhibition",
    gradient: "from-amber-500/20 to-orange-500/20",
  },
  pop_balloon: {
    icon: (
      <div className="relative">
        <span className="text-5xl">🎈</span>
        <Heart className="absolute -bottom-1 -right-1 w-6 h-6 text-pink-500" />
      </div>
    ),
    title: "No Pop Balloon matches yet",
    description: "Pop someone's balloon and wait for them to pop yours back — that's a match!",
    cta: "Pop Balloons",
    link: "/",
    gradient: "from-pink-500/20 to-rose-500/20",
  },
  global_connect: {
    icon: <Globe className="w-12 h-12" />,
    title: "No Global Connect matches yet",
    description: "Travel the world virtually and connect with people in real-time scenes",
    cta: "Explore the World",
    link: "/global-connect",
    gradient: "from-blue-500/20 to-cyan-500/20",
  },
};

export const MatchesEmptyState = ({ filter }: MatchesEmptyStateProps) => {
  const state = EMPTY_STATES[filter];

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 animate-fade-in-up">
      {/* Icon container with gradient */}
      <div className={cn(
        "w-28 h-28 rounded-full flex items-center justify-center mb-6",
        "bg-gradient-to-br shadow-lg",
        state.gradient
      )}>
        <div className="text-foreground/80 animate-float">
          {state.icon}
        </div>
      </div>

      {/* Title */}
      <h2 className="text-xl font-semibold text-center mb-2">
        {state.title}
      </h2>

      {/* Description */}
      <p className="text-muted-foreground text-center max-w-xs mb-8">
        {state.description}
      </p>

      {/* CTA Button */}
      <Link
        to={state.link}
        className={cn(
          "inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium",
          "bg-gradient-to-r from-primary to-primary/80 text-primary-foreground",
          "shadow-lg shadow-primary/25 hover:shadow-primary/40",
          "hover:scale-105 active:scale-95 transition-all duration-200"
        )}
      >
        {state.cta}
        <ArrowRight className="w-4 h-4" />
      </Link>

      {/* Animated floating orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className={cn(
              "absolute rounded-full bg-gradient-to-br animate-float",
              i % 3 === 0 ? "w-16 h-16 from-primary/30 to-primary/10" :
              i % 3 === 1 ? "w-12 h-12 from-pink-500/30 to-pink-500/10" :
              "w-8 h-8 from-blue-500/30 to-blue-500/10"
            )}
            style={{
              left: `${10 + Math.random() * 80}%`,
              top: `${10 + Math.random() * 80}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 2}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
};
